import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calculator, Save, Plus, X, Loader2, Tag, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { API_BASE, formatApiError } from "../components/AuthContext";

const INPUT_CLS = "w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[14px] focus:outline-none focus:border-[var(--ink)]";

const tagLabel = (t) => t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function FeeSchedule() {
  const [schedule, setSchedule] = useState(null);
  const [tags, setTags] = useState([]);
  const [baseFee, setBaseFee] = useState("");
  const [multiYear, setMultiYear] = useState("");
  const [currency, setCurrency] = useState("AUD");
  const [addons, setAddons] = useState({});      // {tag: amount}
  const [newTag, setNewTag] = useState("");
  const [newTagAmt, setNewTagAmt] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API_BASE}/fee-schedule`),
        axios.get(`${API_BASE}/leads/tags`),
      ]);
      setSchedule(s.data); setTags(t.data.tags || []);
      setBaseFee(String(s.data.base_fee));
      setMultiYear(String(s.data.multi_year_per_year));
      setCurrency(s.data.currency || "AUD");
      setAddons({ ...(s.data.tag_addons || {}) });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const setAddon = (key, val) => setAddons((p) => ({ ...p, [key]: val }));
  const removeAddon = (key) => setAddons((p) => {
    const n = { ...p }; delete n[key]; return n;
  });

  const addCustomAddon = () => {
    const slug = newTag.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    const amt = parseFloat(newTagAmt);
    if (!slug) { toast.error("Tag name required"); return; }
    if (Number.isNaN(amt) || amt < 0) { toast.error("Valid amount required"); return; }
    if (addons[slug] !== undefined) { toast.error("That tag already has an addon"); return; }
    setAddons((p) => ({ ...p, [slug]: amt }));
    setNewTag(""); setNewTagAmt("");
  };

  const save = async () => {
    const bf = parseFloat(baseFee);
    const my = parseFloat(multiYear);
    if (Number.isNaN(bf) || bf < 0) { toast.error("Base fee must be a non-negative number"); return; }
    if (Number.isNaN(my) || my < 0) { toast.error("Multi-year per-year must be a non-negative number"); return; }
    const cleanAddons = {};
    for (const [k, v] of Object.entries(addons)) {
      const n = parseFloat(v);
      if (Number.isNaN(n) || n < 0) { toast.error(`Invalid amount for "${k}"`); return; }
      cleanAddons[k] = n;
    }
    setSaving(true);
    try {
      const r = await axios.put(`${API_BASE}/fee-schedule`, {
        base_fee: bf,
        tag_addons: cleanAddons,
        multi_year_per_year: my,
        currency: currency || "AUD",
      });
      setSchedule(r.data);
      toast.success("Fee schedule saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  // All tags present in either the org library or already in addons
  const allTagKeys = Array.from(new Set([...tags, ...Object.keys(addons)]));

  return (
    <div className="px-10 py-12 max-w-[1100px]" data-testid="fee-schedule-page">
      <div className="mb-3">
        <Link to="/leads" className="text-[11px] text-[var(--ink-3)] hover:text-[var(--accent)] inline-flex items-center gap-1" data-testid="back-to-leads">
          <ArrowLeft size={12}/> Back to leads
        </Link>
      </div>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="overline mb-2">Phase 1 · Pricing</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>
            Fee <em className="italic" style={{ color: "var(--accent)" }}>schedule</em>
          </h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
            The auto-calculator runs on every lead — <code className="text-[var(--accent)]">base fee</code> + scenario-tag surcharges + multi-year multiplier. Manual additions stay attached to the lead even after a recalc.
          </p>
        </div>
        <button onClick={save} disabled={saving || loading} className="btn-primary" data-testid="fee-schedule-save">
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
          {saving ? "Saving…" : "Save schedule"}
        </button>
      </div>

      {loading ? (
        <div className="text-[var(--ink-3)] text-sm">Loading…</div>
      ) : !schedule ? (
        <div className="text-[var(--ink-3)] text-sm">No schedule yet.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Base + multi-year */}
          <div className="lg:col-span-1 space-y-5">
            <div className="card p-4">
              <div className="overline mb-3 inline-flex items-center gap-1"><Calculator size={11}/> Core</div>
              <label className="block mb-3">
                <div className="text-[11px] text-[var(--ink-3)] mb-1">Base fee (individual return)</div>
                <input type="number" step="0.01" value={baseFee} onChange={(e) => setBaseFee(e.target.value)} className={INPUT_CLS} data-testid="base-fee-input"/>
              </label>
              <label className="block mb-3">
                <div className="text-[11px] text-[var(--ink-3)] mb-1">Multi-year per extra year</div>
                <input type="number" step="0.01" value={multiYear} onChange={(e) => setMultiYear(e.target.value)} className={INPUT_CLS} data-testid="multi-year-input"/>
                <div className="text-[10px] text-[var(--ink-3)] mt-1">
                  Charged once per year beyond year one. e.g. 3 years × $80 = $160 extra.
                </div>
              </label>
              <label className="block">
                <div className="text-[11px] text-[var(--ink-3)] mb-1">Currency</div>
                <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={6} className={INPUT_CLS} data-testid="currency-input"/>
              </label>
            </div>

            <div className="card p-4">
              <div className="overline mb-2">Example</div>
              <div className="text-[12px] space-y-1">
                <div className="flex justify-between"><span>Base</span><span className="font-mono">${Number(baseFee || 0).toFixed(2)}</span></div>
                <div className="flex justify-between text-[var(--ink-3)]"><span>+ Crypto</span><span className="font-mono">${Number(addons.crypto || 0).toFixed(2)}</span></div>
                <div className="flex justify-between text-[var(--ink-3)]"><span>+ Rental property</span><span className="font-mono">${Number(addons.rental_property || 0).toFixed(2)}</span></div>
                <div className="flex justify-between text-[var(--ink-3)]"><span>+ 1 extra year</span><span className="font-mono">${Number(multiYear || 0).toFixed(2)}</span></div>
                <div className="flex justify-between border-t border-[var(--border)] pt-1 mt-1 font-semibold"><span>Total</span><span className="font-mono">${(
                  Number(baseFee || 0) + Number(addons.crypto || 0) + Number(addons.rental_property || 0) + Number(multiYear || 0)
                ).toFixed(2)}</span></div>
              </div>
            </div>
          </div>

          {/* Tag addons */}
          <div className="lg:col-span-2 space-y-5">
            <div className="card p-4">
              <div className="overline mb-3 inline-flex items-center gap-1"><Tag size={11}/> Scenario tag addons</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="tag-addons-grid">
                {allTagKeys.map((t) => (
                  <div key={t} className="flex items-center gap-2 border border-[var(--border)] rounded-md px-3 py-2" data-testid={`addon-row-${t}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-[var(--ink)] truncate">{tagLabel(t)}</div>
                      <div className="text-[9px] font-mono text-[var(--ink-3)]">{t}</div>
                    </div>
                    <div className="w-24">
                      <input
                        type="number" step="0.01"
                        value={addons[t] ?? ""}
                        placeholder="0"
                        onChange={(e) => setAddon(t, e.target.value)}
                        className={`${INPUT_CLS} text-right text-[12px] py-1.5`}
                        data-testid={`addon-amount-${t}`}
                      />
                    </div>
                    {!tags.includes(t) && (
                      <button onClick={() => removeAddon(t)} className="text-[var(--ink-3)] hover:text-[#B91C1C]" data-testid={`addon-remove-${t}`}>
                        <X size={14}/>
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="border-t border-[var(--border)] pt-3 mt-4">
                <div className="text-[11px] text-[var(--ink-3)] mb-2">Add a custom tag addon</div>
                <div className="flex gap-2 items-center">
                  <input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="tag name (e.g. sole_trader)" className={`${INPUT_CLS} flex-1 text-[12px] py-1.5`} data-testid="new-addon-tag"/>
                  <div className="w-24">
                    <input type="number" step="0.01" value={newTagAmt} onChange={(e) => setNewTagAmt(e.target.value)} placeholder="$" className={`${INPUT_CLS} text-right text-[12px] py-1.5`} data-testid="new-addon-amount"/>
                  </div>
                  <button onClick={addCustomAddon} className="btn-secondary px-3 py-1.5 text-[11px]" data-testid="new-addon-submit">
                    <Plus size={11}/> Add
                  </button>
                </div>
                <div className="text-[10px] text-[var(--ink-3)] mt-2">
                  Tip: addons whose key matches a scenario tag in the library will auto-apply when that tag is selected on a lead.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
